"use client"

import { useState } from "react"
import Link from "next/link"
import { useAlertSubscribe } from "./use-alert-subscribe"

interface RecallAlertFormProps {
  fin: string
  productTitle: string
}

/**
 * Recall alert opt-in. Copy is deliberately safety-neutral: it must never
 * imply a recall exists when none does, and never fear-monger. Renders
 * whenever a FIN exists, independent of active recalls (the RecallBanner
 * above it handles the has-recall case).
 */
export function RecallAlertForm({ fin, productTitle }: RecallAlertFormProps) {
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [honeypot, setHoneypot] = useState("")
  const [optIn, setOptIn] = useState(false)
  const { status, errorMessage, subscribe } = useAlertSubscribe()

  if (!fin) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void subscribe({
      type: "recall_alert",
      email,
      firstName: firstName.trim() || undefined,
      lastName: lastName.trim() || undefined,
      honeypot,
      generalUpdatesOptIn: optIn,
      fin,
    })
  }

  if (status === "success") {
    return (
      <div
        className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4 text-sm text-gray-700"
        data-testid="recall-alert-form"
        data-state="success"
      >
        <p className="font-semibold text-gray-900">Almost there — check your inbox.</p>
        <p className="text-xs text-gray-600 mt-1">
          We just emailed you a confirmation link. Click it to activate your recall alert —
          nothing arrives, and no alerts start, until you confirm.
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4"
      data-testid="recall-alert-form"
    >
      <p className="text-xs font-bold text-gray-900 uppercase tracking-wide">Recall alerts</p>
      <p className="text-xs text-gray-600 mt-0.5">
        Get an email if the CPSC announces a recall affecting this model ({productTitle}).
      </p>
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <input
          name="first_name"
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name (optional)"
          autoComplete="given-name"
          maxLength={60}
          className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 focus:border-[#ff9951] outline-none"
          aria-label="First name (optional)"
        />
        <input
          name="last_name"
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Last name (optional)"
          autoComplete="family-name"
          maxLength={60}
          className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 focus:border-[#ff9951] outline-none"
          aria-label="Last name (optional)"
        />
      </div>
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <input
          name="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 focus:border-[#ff9951] outline-none"
          aria-label="Email address for recall alerts"
        />
        <button
          type="submit"
          disabled={status === "submitting" || !email.trim()}
          className="px-4 py-2 text-xs font-bold uppercase tracking-wide bg-gray-900 hover:bg-gray-700 text-white rounded-md transition-colors disabled:opacity-50"
        >
          {status === "submitting" ? "Saving…" : "Notify me"}
        </button>
      </div>
      {/* Honeypot — hidden from real users; bots that fill it are rejected. */}
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <label className="mt-2 flex items-start gap-2 text-xs text-gray-600">
        <input
          name="general_updates"
          type="checkbox"
          checked={optIn}
          onChange={(e) => setOptIn(e.target.checked)}
          className="mt-0.5"
          data-testid="general-updates-optin"
        />
        Also send me occasional Fridge.com updates
      </label>
      {status === "error" && errorMessage && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {errorMessage}
        </p>
      )}
      <p className="mt-2 text-[11px] text-gray-500">
        We may earn an affiliate commission when you buy through our links. This never affects
        our reviews or comparisons.{" "}
        <Link href="/privacy-policy" className="underline hover:text-gray-700">
          Privacy policy
        </Link>
      </p>
    </form>
  )
}

export default RecallAlertForm
